import { useMemo } from "react";
import BuildOutlined from "@mui/icons-material/BuildOutlined";
import CompareArrowsOutlined from "@mui/icons-material/CompareArrowsOutlined";
import GroupsOutlined from "@mui/icons-material/GroupsOutlined";
import LinkOutlined from "@mui/icons-material/LinkOutlined";
import PaymentsOutlined from "@mui/icons-material/PaymentsOutlined";
import ReceiptLongOutlined from "@mui/icons-material/ReceiptLongOutlined";
import RequestQuoteOutlined from "@mui/icons-material/RequestQuoteOutlined";
import SpaceDashboardOutlined from "@mui/icons-material/SpaceDashboardOutlined";

import { SidebarSectionType } from "../../types/sidebar";
import { useRoles } from "../../utils/auth";
import { useMaintenanceAccess } from "../../hooks/useMaintenanceAccess";

// /invoices redirects to the dashboard, whose Recent distributions replace it.
const allSections: SidebarSectionType[] = [
  {
    title: "Overview",
    pages: [
      {
        href: "/",
        icon: SpaceDashboardOutlined,
        title: "Dashboard",
      },
    ],
  },
  {
    title: "Payments",
    pages: [
      {
        href: "/bills",
        icon: ReceiptLongOutlined,
        title: "Bills",
      },
      {
        href: "/payees",
        icon: GroupsOutlined,
        title: "Payees",
      },
      {
        href: "/payments",
        icon: PaymentsOutlined,
        title: "Payments",
      },
      {
        href: "/taxes2025",
        icon: RequestQuoteOutlined,
        title: "Payments 2025",
        requiredRoles: ["Admin", "Developer"],
      },
    ],
  },
  {
    title: "Reconciliation",
    pages: [
      {
        href: "/csitipalti",
        icon: CompareArrowsOutlined,
        title: "CSI-Tipalti Analysis",
        requiredRoles: ["Admin", "Developer"],
      },
    ],
  },
  {
    title: "Admin",
    pages: [
      {
        href: "/quickbooks-companies",
        icon: LinkOutlined,
        title: "Connect QuickBooks",
        requiredRoles: ["Admin", "Developer"],
      },
      {
        href: "/maintenance",
        icon: BuildOutlined,
        title: "Maintenance",
        operatorOnly: true,
      },
    ],
  },
];

/**
 * The sidebar sections the signed-in user may see. Role-gated items need any of their
 * requiredRoles; operator-only items need the API's Maintenance allow-list (AB#6563).
 * A section whose items are all hidden is dropped.
 */
export const useDashboardItems = (): SidebarSectionType[] => {
  // AB#6563: operator-only pages depend on an API answer, not a token role.
  const { canOperate } = useMaintenanceAccess();
  const roles = useRoles();

  return useMemo(
    () =>
      allSections
        .map((section) => ({
          title: section.title,
          pages: section.pages.filter((page) => {
            if (page.operatorOnly) {
              return canOperate;
            }
            if (!page.requiredRoles || page.requiredRoles.length === 0) {
              return true;
            }
            return page.requiredRoles.some((role) => roles.includes(role));
          }),
        }))
        .filter((section) => section.pages.length > 0),
    [roles, canOperate]
  );
};
